'use client';

import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import useAnimalFilterStore from '@/studbook/store/useAnimalFilterStore';
import useSearch from '@/hooks/studbook/useSearch';
import {
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridColumnOrderChangeParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import useAnimalDisplayType from '@/hooks/studbook/animal/useAnimalDisplayType';
import { theme } from '@/lib/theme';
import { closeSnackbar } from '@/providers';
import useStudbookContext from '@/studbook/store/useStudbookContext';
import useGridStore from '@/studbook/store/useGridStore';
import useSearchStore from '@/studbook/store/useSearchStore';
import WithSkeleton from '@/components/utils/WithSkeleton';
import AnimalSearchResult from '../searchSection/AnimalSearchResult';
import MemberSearchResult from '../searchSection/MemberSearchResult';
import { abortAllPendingGraphQL } from '@/studbook/apolloClient';
import { defaultPublicSearchVisibleFields } from '@/components/member/shared/memberColumnConfig';
import useSearchCount from '@/hooks/studbook/useSearchCount';

export default function SearchResult() {
  const { clearFilter: clearSavedFilter } = useAnimalFilterStore();
  const { orgId } = useStudbookContext();
  const { searchType, openResult, clearSearchResult, setBasicSearch } =
    useSearchStore();
  const {
    paginationModel,
    manualColumnView,
    columnOrder,
    setColumnVisibilityModel,
    setColumnOrder,
    setManualColumnView,
  } = useGridStore();
  const { isMobile } = useAnimalDisplayType();
  const { dataGrid, defaultColumnFieldOrder, setGridSortModel, startSearch } =
    useSearch(orgId, paginationModel);
  const { count, loading } = useSearchCount(orgId);

  const hasNoResult = count === 0 && !loading;

  const clearFilter = () => {
    closeSnackbar && closeSnackbar();
    // Cancel any in-flight GraphQL queries before resetting filters/UI
    abortAllPendingGraphQL();
    setBasicSearch('');
    setGridSortModel([]);
    clearSavedFilter();
    clearSearchResult();
  };

  const noResultMessage = useMemo(() => {
    return (
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Typography
          variant="titlesmall"
          color="error"
          sx={{ paddingBottom: 1, paddingInline: 1 }}
        >
          No search results found. Try searching with different filter options
        </Typography>
      </Box>
    );
  }, []);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setGridSortModel(model);
      startSearch();
    },
    [setGridSortModel, startSearch],
  );

  // Apply stored column order
  const orderedColumns = useMemo(() => {
    if (
      !dataGrid.columns ||
      dataGrid.columns.length === 0 ||
      !manualColumnView ||
      !columnOrder.length
    ) {
      return dataGrid.columns;
    }

    // Create a copy of columns
    const columns = [...dataGrid.columns];
    const orderedCols: typeof columns = [];

    // First, add columns in the stored order
    columnOrder.forEach((fieldName) => {
      const column = columns.find((col) => col.field === fieldName);
      if (column) {
        orderedCols.push(column);
      }
    });

    // Then add any remaining columns that weren't in the stored order
    columns.forEach((column) => {
      if (!columnOrder.includes(column.field)) {
        orderedCols.push(column);
      }
    });

    return orderedCols;
  }, [dataGrid.columns, columnOrder, manualColumnView]);

  useEffect(() => {
    if (manualColumnView || !dataGrid.columns || dataGrid.columns.length === 0)
      return;

    let colViewModel: GridColumnVisibilityModel = {};
    if (searchType === 'animal') {
      dataGrid.columns.forEach((c) => {
        colViewModel[c.field] = (defaultColumnFieldOrder ?? []).includes(
          c.field ?? '',
        );
      });
    } else {
      // For members, only show the default visible fields initially
      dataGrid.columns.forEach((c) => {
        colViewModel[c.field] = defaultPublicSearchVisibleFields.includes(
          c.field ?? '',
        );
      });
    }
    setColumnVisibilityModel(colViewModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGrid.columns, manualColumnView, setColumnVisibilityModel]);

  const handleColumnVisibilityChange = (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails,
  ) => {
    setManualColumnView(true);
    setColumnVisibilityModel(model);
  };

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      // Use the current columns and apply the reorder based on the params
      if (!dataGrid.columns || dataGrid.columns.length === 0) return;

      const { oldIndex, targetIndex } = params;
      const currentColumns = orderedColumns || dataGrid.columns;
      const newOrder = [...currentColumns];

      // Move the column from oldIndex to targetIndex
      const [movedColumn] = newOrder.splice(oldIndex, 1);
      newOrder.splice(targetIndex, 0, movedColumn);

      // Extract the field names in the new order
      const columnFieldOrder = newOrder.map((col) => col.field);

      setColumnOrder(columnFieldOrder);
      setManualColumnView(true);
    },
    [dataGrid.columns, orderedColumns, setColumnOrder, setManualColumnView],
  );

  return (
    <Box
      sx={{
        background: theme.palette.background.default,
        paddingBlock: 1,
        paddingInline: isMobile ? 1 : 2,
        display: openResult ? 'block' : 'none',
        minHeight: 200,
      }}
    >
      {hasNoResult && noResultMessage}
      {!hasNoResult && (
        <Box>
          {!dataGrid || !dataGrid.columns || dataGrid.columns.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="titlesmall">
                No columns to display
              </Typography>
            </Box>
          ) : (
            <WithSkeleton isLoading={loading} useLinearProgress>
              {searchType === 'member' ? (
                <MemberSearchResult
                  clearFilter={clearFilter}
                  handleColumnVisibilityChange={handleColumnVisibilityChange}
                  handleColumnOrderChange={handleColumnOrderChange}
                  orderedColumns={orderedColumns}
                  handleSortModelChange={handleSortModelChange}
                />
              ) : (
                <AnimalSearchResult
                  clearFilter={clearFilter}
                  handleColumnOrderChange={handleColumnOrderChange}
                  handleColumnVisibilityChange={handleColumnVisibilityChange}
                  handleSortModelChange={handleSortModelChange}
                  orderedColumns={orderedColumns}
                />
              )}
            </WithSkeleton>
          )}
        </Box>
      )}
    </Box>
  );
}
